import UserRolesAPI from "@/api/UserRoles/UserRoles";
import { Option, SelectBox } from "@monolith-forensics/monolith-ui";

import { useQuery } from "@tanstack/react-query";
import { FC, ReactNode, useState } from "react";
import { useTheme } from "styled-components";

interface RoleSelectorProps {
  onChange?: (value: string, data: RoleData) => void;
  disabled?: boolean;
  error?: string;
  defaultValue?: string;
  value?: string;
}

type RoleData = {
  id: number;
  name: string;
  description: string;
};

const RoleContent: FC<RoleData> = ({ description }) => {
  const theme = useTheme() as { palette: { text: { secondary: string } } };
  return (
    <div
      style={{
        fontSize: "0.9em",
        marginTop: "4px",
        color: theme.palette.text.secondary,
        lineHeight: "1.2",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {description}
    </div>
  );
};

const RoleSelector: FC<RoleSelectorProps> = ({
  onChange,
  defaultValue,
  value,
  error,
  disabled = false,
}) => {
  const isControlled = value !== undefined;

  const [stateValue, setStateValue] = useState<string | undefined>(
    defaultValue
  );

  const _value = isControlled ? value : stateValue;

  const { data: roles, isLoading } = useQuery({
    queryKey: ["user-roles", "list"],
    queryFn: () => UserRolesAPI.get(),
    select: (data) => data.data,
  });

  const handleChange = (value: string, data: RoleData) => {
    if (!isControlled) {
      setStateValue(value);
    }

    onChange?.(value, data);
  };

  const roleData = roles?.map((role: RoleData) => ({
    label: role.name,
    value: role.id.toString(),
    data: role,
  }));

  return (
    <SelectBox
      key={isLoading ? 1 : 2}
      value={_value?.toString()}
      loading={isLoading}
      onChange={handleChange}
      error={error}
      size="sm"
      variant="outlined"
      label="User Role"
      placeholder="Select a role"
      data={roleData}
      description="Assign user role, for me details see Roles tab"
      disabled={disabled}
      required
      renderOption={(item: string | Option): ReactNode => {
        if (typeof item === "string") {
          return <div>{item}</div>;
        }
        return (
          <div style={{ padding: "5px 0" }}>
            <div>{item.label}</div>
            {item.data && <RoleContent {...item.data} />}
          </div>
        );
      }}
    />
  );
};

export default RoleSelector;
